import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { GoogleMap, useLoadScript, DirectionsRenderer, Marker } from "@react-google-maps/api";
import { toast } from "react-toastify";
import { setOptimizedRoute, previousStep } from "../../../redux/routeFLowSlice";
import { handlePostRequest } from "../../../services/PostTemplate";
import styles from "./OptimizeRouteStep.module.scss";

const mapContainerStyle = {
    width: "100%",
    height: "600px",
};

const defaultCenter = {
    lat: 25.7617,
    lng: -80.1918,
};

// Define libraries outside component to avoid re-renders
const libraries = ["places"];

function OptimizeRouteStep({ deliveryId, onComplete }) {
    const dispatch = useDispatch();
    const generatedStops = useSelector((state) => state.routeFlowSlice.generatedStops);
    const optimizedRoute = useSelector((state) => state.routeFlowSlice.optimizedRoute);
    const [directions, setDirections] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mapCenter, setMapCenter] = useState(defaultCenter);

    // Load Google Maps Script
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });

    useEffect(() => {
        if (isLoaded && !optimizedRoute && generatedStops.length > 0) {
            // Small delay to ensure everything is initialized
            setTimeout(() => {
                optimizeRoute();
            }, 500);
        }
    }, [isLoaded]);

    const optimizeRoute = async () => {
        if (generatedStops.length === 0) {
            toast.error("No stops available to optimize");
            return;
        }

        if (!isLoaded) {
            toast.error("Google Maps is still loading. Please wait...");
            return;
        }

        // Validate that stops have coordinates
        const invalidStops = generatedStops.filter((stop) => !stop.latitude || !stop.longitude || stop.latitude === 0 || stop.longitude === 0);

        if (invalidStops.length > 0) {
            toast.error(`${invalidStops.length} stop(s) have invalid coordinates`);
            return;
        }

        try {
            setLoading(true);
            toast.info("Optimizing route...");

            // Handle single stop case
            if (generatedStops.length === 1) {
                const singleStop = generatedStops[0];

                // Create a simple route data for single stop
                const singleStopData = {
                    optimizedStops: [singleStop],
                    totalDistance: "0.00", // No travel distance
                    totalDuration: 0, // No travel time
                    polyline: null, // No polyline for single stop
                };

                dispatch(setOptimizedRoute(singleStopData));

                // Center map on the single stop
                setMapCenter({
                    lat: singleStop.latitude,
                    lng: singleStop.longitude,
                });

                toast.success("Route created with single stop!");
                setLoading(false);
                return;
            }

            // Prepare waypoints for Google Directions API (for multiple stops)
            const waypoints = generatedStops.map((stop) => ({
                location: new window.google.maps.LatLng(stop.latitude, stop.longitude),
                stopover: true,
            }));

            // Use Google Directions Service
            const directionsService = new window.google.maps.DirectionsService();

            const origin = waypoints[0].location;
            const destination = waypoints[waypoints.length - 1].location;
            const waypointsMiddle = waypoints.slice(1, -1);

            const request = {
                origin,
                destination,
                waypoints: waypointsMiddle.length > 0 ? waypointsMiddle : undefined,
                optimizeWaypoints: true,
                travelMode: window.google.maps.TravelMode.DRIVING,
            };

            directionsService.route(request, (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                    setDirections(result);

                    // Calculate total distance and duration
                    let totalDistance = 0;
                    let totalDuration = 0;

                    result.routes[0].legs.forEach((leg) => {
                        totalDistance += leg.distance.value; // in meters
                        totalDuration += leg.duration.value; // in seconds
                    });

                    // Get optimized order
                    const waypointOrder = result.routes[0].waypoint_order || [];
                    const optimizedStopsOrder = [
                        generatedStops[0], // First stop (origin)
                        ...waypointOrder.map((index) => generatedStops[index + 1]),
                        generatedStops[generatedStops.length - 1], // Last stop (destination)
                    ];

                    const optimizedData = {
                        optimizedStops: optimizedStopsOrder,
                        totalDistance: (totalDistance / 1000).toFixed(2), // km
                        totalDuration: Math.round(totalDuration / 60), // minutes
                        polyline: result.routes[0].overview_polyline,
                    };

                    dispatch(setOptimizedRoute(optimizedData));
                    toast.success("Route optimized successfully!");

                    // Center map on route
                    if (result.routes[0].bounds) {
                        setMapCenter({
                            lat: result.routes[0].bounds.getCenter().lat(),
                            lng: result.routes[0].bounds.getCenter().lng(),
                        });
                    }
                } else {
                    console.error("Directions request failed:", status, result);
                    let errorMessage = "Failed to optimize route";

                    switch (status) {
                        case "ZERO_RESULTS":
                            errorMessage = "No route could be found between the stops";
                            break;
                        case "MAX_WAYPOINTS_EXCEEDED":
                            errorMessage = "Too many stops. Maximum is 25 waypoints";
                            break;
                        case "INVALID_REQUEST":
                            errorMessage = "Invalid route request. Check stop coordinates";
                            break;
                        case "OVER_QUERY_LIMIT":
                            errorMessage = "Google Maps API quota exceeded";
                            break;
                        case "REQUEST_DENIED":
                            errorMessage = "Route request denied. Check API key";
                            break;
                        default:
                            errorMessage = `Failed to optimize route: ${status}`;
                    }

                    toast.error(errorMessage);
                }
                setLoading(false);
            });
        } catch (error) {
            console.error("Error optimizing route:", error);
            toast.error("An error occurred while optimizing the route");
            setLoading(false);
        }
    };

    const handleSaveRoute = async () => {
        if (!optimizedRoute) {
            toast.error("No optimized route to save");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                deliveryId,
                stops: generatedStops,
                optimizedRoute,
            };
            console.log("Saving route with payload:", payload);

            const result = await dispatch(handlePostRequest(payload, "/saveDeliveryRoute", true, true));

            if (result !== "error") {
                toast.success("Route saved successfully!");
                onComplete();
            } else {
                toast.error("Failed to save route");
            }
        } catch (error) {
            console.error("Error saving route:", error);
            toast.error("An error occurred while saving the route");
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        dispatch(previousStep());
    };

    const getMarkerIcon = (status) => {
        switch (status) {
            case "Delivered":
                return "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
            case "Incomplete":
                return "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
            default:
                return "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
        }
    };

    // Handle loading error
    if (loadError) {
        return (
            <div className={styles.errorContainer}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: "3rem", color: "#e74c3c" }}></i>
                <h3>Error Loading Google Maps</h3>
                <p>Please check your API key and internet connection</p>
                <Button label="Go Back" icon="pi pi-arrow-left" onClick={handleBack} />
            </div>
        );
    }

    // Handle loading state
    if (!isLoaded) {
        return (
            <div className={styles.loadingContainer}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "3rem", color: "#667eea" }}></i>
                <p>Loading Google Maps...</p>
            </div>
        );
    }

    return (
        <div className={styles.optimizeContainer}>
            <div className={styles.header}>
                <h2>Optimized Route</h2>
                <p>Review and finalize your delivery route</p>
            </div>

            {/* Route Summary */}
            {optimizedRoute && (
                <div className={styles.routeSummary}>
                    <div className={styles.summaryCard}>
                        <i className="pi pi-map"></i>
                        <div>
                            <span className={styles.label}>Total Distance</span>
                            <span className={styles.value}>{optimizedRoute.totalDistance} km</span>
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <i className="pi pi-clock"></i>
                        <div>
                            <span className={styles.label}>Estimated Duration</span>
                            <span className={styles.value}>{optimizedRoute.totalDuration} min</span>
                        </div>
                    </div>

                    <div className={styles.summaryCard}>
                        <i className="pi pi-map-marker"></i>
                        <div>
                            <span className={styles.label}>Total Stops</span>
                            <span className={styles.value}>{generatedStops.length}</span>
                        </div>
                    </div>

                    <Button label="Re-Optimize" icon="pi pi-refresh" onClick={optimizeRoute} loading={loading} className="p-button-outlined" disabled={loading} />
                </div>
            )}

            {/* Map */}
            <div className={styles.mapContainer}>
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={12}
                    options={{
                        zoomControl: true,
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: true,
                    }}
                >
                    {/* Directions */}
                    {directions && <DirectionsRenderer directions={directions} />}

                    {/* Stop Markers (only show if no directions) */}
                    {!directions &&
                        generatedStops.map((stop, index) => (
                            <Marker
                                key={stop.stopId}
                                position={{
                                    lat: stop.latitude,
                                    lng: stop.longitude,
                                }}
                                label={{
                                    text: (index + 1).toString(),
                                    color: "white",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                }}
                                icon={{
                                    url: getMarkerIcon(stop.status),
                                }}
                                title={`Stop ${stop.stopNumber}: ${stop.clientName}`}
                            />
                        ))}
                </GoogleMap>
            </div>

            {/* Stop Legend */}
            <div className={styles.legend}>
                <h4>Stop Status Legend:</h4>
                <div className={styles.legendItems}>
                    <div className={styles.legendItem}>
                        <span className={styles.marker} style={{ background: "#FFEB3B" }}></span>
                        <span>Not Delivered</span>
                    </div>
                    <div className={styles.legendItem}>
                        <span className={styles.marker} style={{ background: "#4CAF50" }}></span>
                        <span>Delivered</span>
                    </div>
                    <div className={styles.legendItem}>
                        <span className={styles.marker} style={{ background: "#F44336" }}></span>
                        <span>Incomplete</span>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className={styles.navigation}>
                <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} className="p-button-outlined" disabled={loading} />
                <Button label="Save & Complete" icon="pi pi-check" iconPos="right" onClick={handleSaveRoute} loading={loading} disabled={!optimizedRoute} />
            </div>
        </div>
    );
}

export default OptimizeRouteStep;
